import { useEffect } from "react";

import { useTranslation } from "next-i18next";
import type { i18n as I18nInstance } from "i18next";

type LocaleModule = { default?: object } | object;

const inflightLoads = new Map<string, Promise<void>>();

function localeFolder(language: string | undefined) {
    return (language || "en").split("-")[0] || "en";
}

function unwrapLocaleModule(mod: LocaleModule) {
    return (mod as { default?: object }).default ?? mod;
}

function hasFilledBundle(i18n: I18nInstance, language: string, ns: string) {
    if (!i18n.hasResourceBundle(language, ns)) {
        return false;
    }

    const bundle = i18n.getResourceBundle(language, ns);
    return Boolean(bundle && typeof bundle === "object" && Object.keys(bundle).length);
}

/**
 * Browser `i18n.loadNamespaces` cannot read filesystem `localePath`.
 * Webpack/turbopack glob: core dist locales for any language + namespace.
 * Missing locale+ns falls back to `en`.
 */
async function importLocaleJson(language: string, ns: string) {
    const load = (lng: string, name: string) => {
        return import(`@vahak/core/dist/locales/${lng}/${name}.json`) as Promise<LocaleModule>;
    };

    try {
        return unwrapLocaleModule(await load(language, ns));
    } catch {
        if (language === "en") {
            return undefined;
        }

        try {
            return unwrapLocaleModule(await load("en", ns));
        } catch {
            return undefined;
        }
    }
}

function loadNamespace(i18n: I18nInstance, language: string, ns: string) {
    const key = `${language}:${ns}`;
    const pending = inflightLoads.get(key);

    if (pending) {
        return pending;
    }

    const job = (async () => {
        if (hasFilledBundle(i18n, language, ns)) {
            return;
        }

        const resources = await importLocaleJson(language, ns);

        if (resources && typeof resources === "object") {
            i18n.addResourceBundle(language, ns, resources, true, true);
        }
    })().finally(() => {
        inflightLoads.delete(key);
    });

    inflightLoads.set(key, job);
    return job;
}

/**
 * Load namespace JSON the route did not SSR.
 * Safe to call from many surfaces — filled bundles and in-flight requests are skipped.
 */
export async function loadI18nNamespaces(i18n: I18nInstance | undefined, namespaces: readonly string[]) {
    if (!i18n || !namespaces.length) {
        return;
    }

    const language = localeFolder(i18n.language || i18n.resolvedLanguage);
    const unique = [...new Set(namespaces)];

    await Promise.all(unique.map((ns) => loadNamespace(i18n, language, ns)));
}

function toNsList(namespaces: string | readonly string[]) {
    return typeof namespaces === "string" ? [namespaces] : namespaces;
}

/**
 * Client-side namespace backup when a surface mounts without those files in SSR.
 */
export function useLoadI18nNamespaces(namespaces: string | readonly string[], enabled = true) {
    const { i18n } = useTranslation();
    const nsList = toNsList(namespaces);
    const nsKey = nsList.join(",");
    const language = localeFolder(i18n.language || i18n.resolvedLanguage);

    useEffect(() => {
        if (!enabled || !nsKey) {
            return;
        }

        void loadI18nNamespaces(i18n, nsKey.split(","));
    }, [enabled, i18n, language, nsKey]);
}

/**
 * `useTranslation` plus runtime load if this route skipped the namespace in SSR.
 * Shared/global components should use this instead of `useTranslation` alone.
 */
export function useRuntimeTranslation(namespaces: string | readonly string[], enabled = true) {
    const nsList = toNsList(namespaces);
    useLoadI18nNamespaces(nsList, enabled);
    return useTranslation(typeof namespaces === "string" ? namespaces : [...nsList]);
}

export default useLoadI18nNamespaces;
